/**
 * useChartSelection — Shared hook for chart renderer selection state.
 *
 * Created s67: extracted from BarChart/LineChart/PieChart renderers
 * to eliminate ~60 lines of duplicated selection + validation lifecycle.
 *
 * Handles:
 * - Selected index state with toggle
 * - Reset on question change
 * - Validation rising-edge detection
 * - Ready state notification
 * - Keyboard navigation (ArrowLeft/Right, Enter/Space)
 */

import { useEffect, useState, useCallback } from 'react'
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit'

interface UseChartSelectionProps {
  questionId: string
  itemCount: number
  idPrefix: string // e.g. 'bar-option', 'line-option', 'pie-option'
  onAnswer: (answer: string | number) => void
  onReadyChange?: (ready: boolean) => void
  shouldValidate?: boolean
}

export function useChartSelection({
  questionId,
  itemCount,
  idPrefix,
  onAnswer,
  onReadyChange,
  shouldValidate,
}: UseChartSelectionProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

  // Reset on question change
  useEffect(() => {
    setSelectedIndex(null)
  }, [questionId])

  // Notify readiness
  useEffect(() => {
    onReadyChange?.(selectedIndex !== null)
  }, [selectedIndex, onReadyChange])

  // Rising-edge validation
  useEdgeTriggeredSubmit(
    selectedIndex !== null ? String(selectedIndex) : '',
    shouldValidate,
    onAnswer,
    questionId,
  )

  const handleItemClick = useCallback((_data: unknown, index: number) => {
    setSelectedIndex((prev) => (prev === index ? null : index))
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleItemClick(null, index)
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault()
        const next = (index + 1) % itemCount
        document.getElementById(`${idPrefix}-${next}`)?.focus()
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault()
        const prev = (index - 1 + itemCount) % itemCount
        document.getElementById(`${idPrefix}-${prev}`)?.focus()
      }
    },
    [itemCount, idPrefix, handleItemClick],
  )

  return { selectedIndex, handleItemClick, handleKeyDown }
}
